/**
 * Coordenadas de um repositório extraídas do endereço que a pessoa já tem na mão.
 *
 * `project` só é preenchido pelo provedor que o carrega na URL — Azure DevOps. Nos
 * demais volta vazio, e o formulário nem mostra o campo.
 */
export interface RepositoryCoordinates {
  org: string;
  project: string;
  name: string;
}

/**
 * Lê a URL de um repositório e devolve as coordenadas que o cadastro pede.
 *
 * Existe porque o GitHub não permite buscar repositórios em nome do usuário — listar com
 * a credencial de serviço exporia, numa tela administrativa, o nome de todo repositório
 * privado da organização. Sem navegação, o cadastro é manual, e quem cadastra precisava
 * quebrar na cabeça um endereço que já tinha inteiro no navegador.
 *
 * Aceita o que de fato se copia:
 *
 *   https://github.com/org/repo              a URL do repositório
 *   https://github.com/org/repo.git          a de clone
 *   git@github.com:org/repo.git              a forma SSH
 *   org/repo                                 o par cru, para quem prefere digitar
 *
 * E as formas com projeto:
 *
 *   https://dev.azure.com/org/projeto/_git/repo
 *   https://org.visualstudio.com/projeto/_git/repo
 *   https://host/projects/PROJ/repos/repo/browse
 *
 * Devolve null para o que não reconhece, e quem chama simplesmente não preenche nada — o
 * campo é atalho, não obrigação: os campos de coordenada continuam editáveis à mão.
 */
export function parseRepositoryUrl(input: string): RepositoryCoordinates | null {
  const raw = String(input ?? '').trim();
  if (raw === '') {
    return null;
  }

  // A forma SSH não é URL: `git@host:org/repo.git` tem `:` onde uma URL teria porta.
  // Trocar por `/` a normaliza sem precisar de um segundo caminho de código.
  const normalized = raw
    .replace(/^[a-z0-9._-]+@([^:]+):/i, 'https://$1/')
    .replace(/^(https?|ssh):\/\//i, '');

  const withoutQuery = normalized.split(/[?#]/)[0];
  const segments = withoutQuery.split('/').filter(Boolean);
  if (segments.length === 0) {
    return null;
  }

  // O par cru não traz host: `org/repo` tem exatamente dois segmentos e nenhum ponto no
  // primeiro. Um host sempre tem ponto ou é `localhost`.
  const looksLikeHost = segments[0].includes('.') || segments[0].toLowerCase() === 'localhost';
  const host = looksLikeHost ? segments[0].toLowerCase() : '';
  const path = looksLikeHost ? segments.slice(1) : segments;
  if (path.length < 2) {
    return null;
  }

  // Azure DevOps: .../{org}/{projeto}/_git/{repo}. O `_git` é o separador, e o que vem
  // antes dele pode ter um segmento a mais (`/tfs/collection/...`), então a leitura é de
  // trás para frente.
  //
  // No endereço antigo — {org}.visualstudio.com/{projeto}/_git/{repo} — a organização não
  // está no caminho, e sim no subdomínio: sobra um segmento só antes do `_git`. Sem este
  // caso o cadastro nasceria sem org, e o identificador derivado sairia diferente do de um
  // cadastro feito pela URL nova para o MESMO repositório.
  const gitAt = path.findIndex(segment => segment.toLowerCase() === '_git');
  if (gitAt >= 1 && path.length > gitAt + 1) {
    const fromHost = host.endsWith('.visualstudio.com') ? host.split('.')[0] : '';
    const org = gitAt >= 2 ? path[gitAt - 2] : fromHost;
    return coordinates(org, path[gitAt - 1], path[gitAt + 1]);
  }

  // O resto é `{org}/{repo}`. O que vier depois é navegação dentro do repositório —
  // /tree/main, /issues, /pull/42 — e não muda quem ele é: só os dois primeiros segmentos
  // formam a identidade. Ignorar o resto é o comportamento natural de ler os dois
  // primeiros, e não um caso tratado à parte.
  return coordinates(path[0], '', path[1]);
}

function coordinates(org: string, project: string, name: string): RepositoryCoordinates | null {
  // O caminho vem percent-encoded, e nome de projeto com espaço é comum no Azure DevOps:
  // "Engenharia Protheus" viaja como "Engenharia%20Protheus". Sem decodificar, o `%20`
  // entra no cadastro como se fosse parte do nome, o identificador derivado sai diferente
  // do que a listagem produziria para o MESMO repositório, e a chamada ao provedor procura
  // um projeto que não existe.
  //
  // decodeURIComponent recusa sequência inválida (um `%` solto no nome), e nesse caso o
  // valor cru é melhor que erro: o campo é editável.
  const decode = (value: string) => {
    try {
      return decodeURIComponent(value);
    } catch {
      return value;
    }
  };
  const clean = (value: string) => decode(String(value ?? '').trim()).trim().replace(/\.git$/i, '');
  const parsed = { org: clean(org), project: clean(project), name: clean(name) };
  return parsed.name === '' ? null : parsed;
}

/**
 * Substitui o `p-pattern` padrão do `po-url`.
 *
 * O padrão do componente não prevê usuário antes do host, e o endereço de clone HTTPS que
 * o Bitbucket Cloud oferece por padrão já vem assim: `https://usuario@bitbucket.org/...`.
 * Colar exatamente o que o próprio provedor entrega marcava o campo como inválido, mesmo o
 * endereço sendo reconhecido normalmente por parseRepositoryUrl acima. O restante do padrão
 * é idêntico ao do componente — só o grupo de usuário (`usuario` ou `usuario:senha` seguido
 * de `@`) foi inserido entre o protocolo e o host.
 *
 * O rótulo de domínio é `[a-z\d]+(-[a-z\d]+)*`, e não a forma do próprio po-url
 * (`[a-z\d]([a-z\d-]*[a-z\d])*`): aquela tem duas repetições aninhadas sobre classes de
 * caractere que se sobrepõem, o gatilho clássico de backtracking catastrófico. Uma string
 * sem ponto nem host válido (um workspace digitado antes de completar a URL, por exemplo)
 * trava a aba por segundos a partir de ~30 caracteres — confirmado localmente, e o
 * componente valida a cada tecla. A forma aqui aceita exatamente os mesmos domínios, só sem
 * a ambiguidade.
 *
 * Vendorizado a partir do `pattern` de `PoUrlComponent` em `@po-ui/ng-components@16.16.0`
 * (a versão travada em package.json, sem `^`/`~` — atualizá-la é sempre um diff de uma
 * linha visível na revisão). `po-url` não expõe o próprio pattern como algo importável
 * para compor em vez de copiar; se essa versão mudar, revisitar este arquivo e conferir se
 * o pattern nativo mudou de forma (`node_modules/@po-ui/ng-components/.../po-url.component.mjs`).
 */
export const REPOSITORY_URL_PATTERN =
  '^((https|http):\\/\\/)?' + // protocolo
  '([a-zA-Z\\d._~%+-]+(:[a-zA-Z\\d._~%+-]*)?@)?' + // usuário (opcional)
  '((([a-z\\d]+(-[a-z\\d]+)*)\\.)+[a-z]{2,}|' + // domínio
  '((\\d{1,3}\\.){3}\\d{1,3}))' + // ou IPv4
  '(\\:\\d+)?(\\/[-A-Za-z\\d%_.~+]*)*' + // porta e caminho
  '(\\?[;&A-Za-z\\d%_.~+=-]*)?' + // query string
  '(\\#[-A-Za-z\\d_]*)?$';
